import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Dialog,Box} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { styled } from '@mui/material/styles'; 
import BtnPrimary from "./PrimaryButton";
import BtnSecondary from "./SecondaryButton";

import { tokens } from "../theme";
const TextBox = styled(Box)({
    display: "block",
});

/**
 * `past` is "past dates are allowed" — the default is future-only, which suits
 * the policy start dates and offboarding dates that make up most callers.
 * `disableFuture` is the mirror, for a field like a date of birth that cannot
 * be ahead of today. `openTo="year"` is worth passing with it: a calendar that
 * opens on this month is forty years of clicking away from a birth year.
 */
function DateSelectDialog({ open, onClose, onSelect, past, disableFuture, value, openTo = 'day' }) {
  const [selectedDate, setSelectedDate] = useState(null);

  // Seeded from the field each time it opens. The dialog stays mounted, so
  // without this its state is whatever was last chosen — or null on a first
  // open of a field that already has a value, which showed nothing selected
  // and started the calendar on today. For a birth date that meant navigating
  // back several hundred months on every visit.
  useEffect(() => {
    if (!open) return;
    const parsed = value ? dayjs(value) : null;
    setSelectedDate(parsed && parsed.isValid() ? parsed : null);
  }, [open, value]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleContinue = () => {
    // Guarded: this used to call .format() straight out, so pressing Add Date
    // without choosing one threw a TypeError on null and took the dialog down.
    // The button is disabled in that state as well — this is the backstop.
    if (!selectedDate || !selectedDate.isValid()) return;
    onSelect(selectedDate.format('YYYY-MM-DD'));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 4px 12px -4px rgba(42,29,34,0.16)",
          padding: "1.5rem"
        }
      }}
    >
        <TextBox sx={{width:"100%", textAlign:"center", fontSize: "1rem", lineHeight: 1.7, fontWeight:"500", color: "#5E2540", marginBottom: "1.5rem" }}>
          Select Date
        </TextBox>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDatePicker
            onChange={handleDateChange}
            value={selectedDate}
            openTo={openTo}
            showDaysOutsideCurrentMonth
            disablePast={!past}
            disableFuture={Boolean(disableFuture)}
            disableHighlightToday
            dayOfWeekFormatter={(_day, weekday) => `${weekday.format('ddd')}`}
            sx={{
              width: "100%",
              border: "none",
              padding: 0,
              "& .MuiPickersToolbar-root": {
                display: "none"
              },
              "& .MuiDialogActions-root": {
                display: "none"
              },
              "& .MuiPickersLayout-contentWrapper":{
                borderRadius:"12px",
                border:"1px solid #EAD8CC",
                boxShadow: "0 16px 40px -20px rgba(42,29,34,0.22)",
              },
              "& .MuiDateCalendar-root":{
                width:"477px",
              },
              "& .MuiTypography-root":{
                fontSize: "0.8125rem",
                lineHeight:1.5,
                marginInline: "0.75rem",
                marginBottom: "0.25rem",
                color:tokens.infoText
              },
              "& .MuiButtonBase-root":{
                fontSize: "0.8125rem",
                lineHeight:1.5,
                marginInline: "0.75rem",
                marginBottom: "0.25rem",
              }
            }}
          />
        </LocalizationProvider>
        <Box style={{display:"flex", gap: "1.5rem", justifyContent:"space-between", width:"100%", marginTop: "1.5rem"}}>
           <BtnPrimary onClick={handleContinue} disabled={!selectedDate} sx={{flex:"1", borderRadius: '8px' }}>Add Date</BtnPrimary>
           <BtnSecondary onClick={onClose} sx={{ flex:"1", borderRadius: '8px' }}>Cancel</BtnSecondary>
        </Box>
    </Dialog>
  );
}

export default DateSelectDialog;

